import React from 'react'
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material'
import { InfoOutlined } from '@mui/icons-material'
import { TELEMETRY_API } from '@/constant/constants'

import {
  MOISTURE_RAMP,
  MOISTURE_MIN,
  MOISTURE_MAX,
  STATUS_GOOD,
  STATUS_WARNING,
  STATUS_SERIOUS,
  STATUS_CRITICAL,
  OVER_SUPPLIED,
  NO_DATA_INK,
  NO_DATA_FILL,
  colorForMoisture,
  inkForMoisture,
  colorForWaterBalance,
  colorForTemp,
  colorForOxygenPercent,
  cleanSensorName,
} from '../../soilMoistureScale'
import GlassCard from './GlassCard'

const API_BASE = `${TELEMETRY_API}/api`

export type SMPoint = {
  ts: number
  moisture?: number
  temperature?: number
  salinity?: number
  waterBalance?: number
  oxygen?: number
}

function coalesceNumber(v: any): number | undefined {
  if (v === null || v === undefined) return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}
function minuteBucket(ts: number): number {
  return Math.floor(ts / 60000) * 60000
}
function pickLower(obj: any, keys: string[]): any {
  if (!obj) return undefined
  const low: any = {}
  for (const k of Object.keys(obj)) low[k.toLowerCase()] = (obj as any)[k]
  for (const k of keys) {
    const v = low[k.toLowerCase()]
    if (v !== undefined) return v
  }
}
function parseSMTagList(json: any): SMPoint[] {
  const raw = json?.data
  if (!raw) return []
  if (!Array.isArray(raw) && typeof raw === 'object') {
    const byBucket = new Map<number, SMPoint>()
    for (const [k, arr] of Object.entries(raw as Record<string, any>)) {
      if (!Array.isArray(arr)) continue
      const key = k.toLowerCase()
      const field =
        key.includes('water_balance') || key.includes('waterbalance')
          ? 'waterBalance'
          : key.includes('moist')
            ? 'moisture'
            : key.includes('temp')
              ? 'temperature'
              : key.includes('sal') || key.includes('ec')
                ? 'salinity'
                : key.includes('oxygen') || key.includes('o2')
                  ? 'oxygen'
                  : null
      if (!field) continue
      for (const item of arr) {
        const val = coalesceNumber(
          pickLower(item, ['value', 'val', 'v', 'reading', 'y']),
        )
        const tStr =
          pickLower(item, [
            'time',
            'timestamp',
            'ts',
            'date',
            'datetime',
            'dateofrecord',
            'recordedat',
            'createdat',
          ]) || ''
        const ts = new Date(tStr).getTime()
        if (!Number.isFinite(ts)) continue
        const b = minuteBucket(ts)
        const rec = byBucket.get(b) ?? { ts: b }
        let vNum = val
        if (field === 'oxygen' && vNum !== undefined) {
          vNum = vNum * 100
        }
        ;(rec as any)[field] = vNum
        byBucket.set(b, rec)
      }
    }
    return [...byBucket.values()].sort((a, b) => a.ts - b.ts)
  }
  const rows: any[] = Array.isArray(raw) ? raw : []
  const out: SMPoint[] = []
  for (const r of rows) {
    const tStr =
      pickLower(r, [
        'time',
        'timestamp',
        'ts',
        'date',
        'datetime',
        'dateofrecord',
        'recorddate',
        'date_time',
      ]) || ''
    const ts = new Date(tStr).getTime()
    if (!Number.isFinite(ts)) continue
    const b = minuteBucket(ts)
    const rec: SMPoint = { ts: b }
    const m = coalesceNumber(
      pickLower(r, ['moisture', 'soil_moisture', 'sm', 'moist']),
    )
    const t = coalesceNumber(
      pickLower(r, ['temperature', 'temp', 'soil_temperature', 't']),
    )
    const s = coalesceNumber(
      pickLower(r, ['salinity', 'ec', 'soil_salinity', 'sal']),
    )
    const wb = coalesceNumber(
      pickLower(r, ['water_balance', 'waterbalance', 'wb', 'waterBal']),
    )
    const o = coalesceNumber(pickLower(r, ['oxygen', 'o2']))
    if (m !== undefined) rec.moisture = m
    if (t !== undefined) rec.temperature = t
    if (s !== undefined) rec.salinity = s
    if (wb !== undefined) rec.waterBalance = wb
    if (o !== undefined) rec.oxygen = o * 100
    out.push(rec)
  }
  return out.sort((a, b) => a.ts - b.ts)
}

function summarizeLatestSM(series: SMPoint[]) {
  if (!series.length) return {}
  let latestTs: number | undefined
  let m: number | undefined
  let t: number | undefined
  let s: number | undefined
  let wb: number | undefined
  let o: number | undefined
  for (let i = series.length - 1; i >= 0; i--) {
    const p = series[i]
    if (latestTs === undefined) latestTs = p.ts
    if (m === undefined && typeof p.moisture === 'number') m = p.moisture
    if (t === undefined && typeof p.temperature === 'number') t = p.temperature
    if (s === undefined && typeof p.salinity === 'number') s = p.salinity
    if (wb === undefined && typeof p.waterBalance === 'number')
      wb = p.waterBalance
    if (o === undefined && typeof p.oxygen === 'number') o = p.oxygen
    if (
      m !== undefined &&
      t !== undefined &&
      s !== undefined &&
      wb !== undefined
    )
      break
  }
  return {
    ts: latestTs,
    moisture: m,
    temperature: t,
    salinity: s,
    waterBalance: wb,
    oxygen: o,
  }
}

export type SMAsset = {
  id: number
  name: string
}
async function fetchSMAssets(): Promise<SMAsset[]> {
  const url = `${API_BASE}/Asset/list?EntityId=114&PageSize=1000&PageIndex=1`
  const res = await fetch(url, {
    mode: 'cors',
    headers: { 'X-API-Key': 'api-read-key-3456789012' },
  })
  const json = await res.json()
  const data: SMAsset[] = Array.isArray(json?.data) ? json.data : []
  return data
}
async function fetchSMSeries(
  assetId: number,
  fromISO: string,
  toISO: string,
  signal?: AbortSignal,
): Promise<SMPoint[]> {
  const params = new URLSearchParams()
  params.set('AssetId', String(assetId))
  params.set('PageSize', '5000')
  params.set('PageIndex', '1')
  params.set('FromTime', fromISO)
  params.set('ToTime', toISO)
  params.set('TimeBucket', '1')
  params.append('Names', 'temperature')
  params.append('Names', 'salinity')
  params.append('Names', 'moisture')
  params.append('Names', 'water_balance')
  params.append('Names', 'oxygen')
  const url = `${API_BASE}/Tag/list?${params.toString()}`
  const res = await fetch(url, {
    mode: 'cors',
    signal,
    headers: { 'X-API-Key': 'api-read-key-3456789012' },
  })
  const json = await res.json()
  return parseSMTagList(json)
}

const BASE_PURPLE = '#6366F1'
function hexToRgb(hex: string) {
  const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex)
  if (!m) return { r: 99, g: 102, b: 241 }
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) }
}
function rgbToHsl(r: number, g: number, b: number) {
  r /= 255
  g /= 255
  b /= 255
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b)
  let h = 0,
    s = 0,
    l = (max + min) / 2
  const d = max - min
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }
  return { h, s, l }
}
function hslToHex(h: number, s: number, l: number) {
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  let r: number, g: number, b: number
  if (s === 0) {
    r = g = b = l
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }
  const toHex = (x: number) =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}
function shadeBy(baseHex: string, t: number) {
  const { r, g, b } = hexToRgb(baseHex)
  const { h, s } = rgbToHsl(r, g, b)
  const Lmin = 0.35,
    Lmax = 0.85
  const l = Lmax - (Lmax - Lmin) * Math.max(0, Math.min(1, t))
  return hslToHex(h, s, l)
}
function colorForSalinity(v?: number) {
  if (v == null) return 'transparent'
  const t = Math.max(0, Math.min(1, (v - 0) / (4 - 0)))
  return shadeBy(BASE_PURPLE, t)
}

type Mode = 'moisture' | 'waterBalance' | 'temperature' | 'salinity' | 'oxygen'

const formatValue = (mode: Mode, v: number): React.ReactNode => {
  switch (mode) {
    case 'moisture':
      return (
        <>
          {v.toFixed(2)}
          <span>%</span>
        </>
      )
    case 'waterBalance':
      return v.toFixed(2)
    case 'temperature':
      return (
        <>
          {v.toFixed(1)}
          <span>°C</span>
        </>
      )
    case 'salinity':
      return (
        <>
          {v.toFixed(2)}
          <span>dS/m</span>
        </>
      )
    case 'oxygen':
      return (
        <>
          {v.toFixed(2)}
          <span>%</span>
        </>
      )
  }
}

function colorForOxygen(v?: number) {
  if (v == null) return 'rgba(148,163,184,0.35)'
  // v is oxygen percent
  if (v > 18)
    return '#15803D' // deep green
  else if (v >= 15)
    return '#4ADE80' // light green
  else if (v >= 12)
    return '#FACC15' // yellow
  else if (v >= 8)
    return '#FB923C' // orange
  else if (v >= 5)
    return '#F97373' // red
  else return '#7C3AED' // dark red / purple
}
// Tooltip carries the raw name plus every metric we have, whatever the mode
const tooltipFor = (s: {
  name: string
  moisture?: number
  waterBalance?: number
  temperature?: number
  salinity?: number
  oxygen?: number
}) => {
  const lines = [s.name]
  if (typeof s.moisture === 'number')
    lines.push(`Soil Moisture: ${s.moisture.toFixed(2)}%`)
  if (typeof s.waterBalance === 'number')
    lines.push(`Water Balance: ${s.waterBalance.toFixed(2)}`)
  if (typeof s.temperature === 'number')
    lines.push(`Temperature: ${s.temperature.toFixed(1)}°C`)
  if (typeof s.salinity === 'number')
    lines.push(`Salinity: ${s.salinity.toFixed(2)} dS/m`)
  if (typeof s.oxygen === 'number')
    lines.push(`Oxygen: ${s.oxygen.toFixed(2)}%`)
  if (lines.length === 1) lines.push('No data in the last 24h')
  return lines.join('\n')
}

const MODE_BUTTONS: { value: Mode; label: string; title: string }[] = [
  { value: 'moisture', label: 'M', title: 'Soil moisture' },
  { value: 'waterBalance', label: 'WB', title: 'Water balance' },
  { value: 'temperature', label: 'T', title: 'Temperature' },
  { value: 'salinity', label: 'S', title: 'Salinity' },
  { value: 'oxygen', label: 'O', title: 'Oxygen' },
]

// The card header names whichever metric is on screen, so the title never
// disagrees with the cells
const MODE_TITLES: Record<Mode, { title: string; unit: string }> = {
  moisture: { title: 'Soil Moisture', unit: '%' },
  waterBalance: { title: 'Water Balance', unit: 'ratio' },
  temperature: { title: 'Soil Temperature', unit: '°C' },
  salinity: { title: 'Salinity', unit: 'dS/m' },
  oxygen: { title: 'Soil Oxygen', unit: '%' },
}

const formatClock = (ts?: number) => {
  if (!ts) return null
  const d = new Date(ts)
  return `${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes(),
  ).padStart(2, '0')}`
}

// Persistent legend per mode — identity is never carried by color alone
const LEGENDS: Record<Mode, { color: string; label: string }[]> = {
  moisture: [
    { color: MOISTURE_RAMP[0], label: `≤${MOISTURE_MIN}%` },
    { color: MOISTURE_RAMP[1], label: '16%' },
    { color: MOISTURE_RAMP[2], label: '22%' },
    { color: MOISTURE_RAMP[3], label: '28%' },
    { color: MOISTURE_RAMP[4], label: `≥${MOISTURE_MAX}%` },
  ],
  waterBalance: [
    { color: STATUS_CRITICAL, label: '<0.25' },
    { color: STATUS_WARNING, label: '0.25–0.5' },
    { color: STATUS_GOOD, label: '0.5–1' },
    { color: OVER_SUPPLIED, label: '>1' },
  ],
  temperature: [
    { color: STATUS_GOOD, label: '≤33°C' },
    { color: STATUS_WARNING, label: '>33°C' },
  ],
  salinity: [
    { color: shadeBy(BASE_PURPLE, 0), label: '0 dS/m' },
    { color: shadeBy(BASE_PURPLE, 0.5), label: '2 dS/m' },
    { color: shadeBy(BASE_PURPLE, 1), label: '≥4 dS/m' },
  ],
  oxygen: [
    { color: '#7C3AED', label: '<5%' },
    { color: STATUS_CRITICAL, label: '5–8%' },
    { color: STATUS_SERIOUS, label: '8–12%' },
    { color: STATUS_WARNING, label: '12–15%' },
    { color: '#4ADE80', label: '15–18%' },
    { color: STATUS_GOOD, label: '>18%' },
  ],
}

export default function SoilMoistureHeatmapCard() {
  const [mode, setMode] = React.useState<Mode>('moisture')
  const handleModeChange = (_: unknown, next: Mode | null) => {
    if (next) setMode(next)
  }
  const gridRef = React.useRef<HTMLDivElement | null>(null)
  const [gridCols, setGridCols] = React.useState(8)
  const [cellW, setCellW] = React.useState(48)
  const [cellH, setCellH] = React.useState(48)
  const [assets, setAssets] = React.useState<SMAsset[]>([])
  const [latest, setLatest] = React.useState(
    new Map<
      number,
      {
        name: string
        ts?: number
        moisture?: number
        temperature?: number
        salinity?: number
        waterBalance?: number
        oxygen?: number
      }
    >(),
  )

  React.useLayoutEffect(() => {
    if (!gridRef.current) return
    const n = Math.max(1, assets.length)
    const el = gridRef.current
    const ro = new ResizeObserver(() => {
      const W = el.clientWidth
      const H = el.clientHeight
      if (W <= 0 || H <= 0) return
      const GAP = 2
      let bestC = 1
      let bestW = 0
      let bestH = 0
      let bestArea = 0
      for (let c = n; c >= 1; c--) {
        const r = Math.ceil(n / c)
        const usableW = W - Math.max(0, c - 1) * GAP
        const usableH = H - Math.max(0, r - 1) * GAP
        const w = Math.floor(usableW / c)
        const h = Math.floor(usableH / r)
        if (w <= 0 || h <= 0) continue
        // Landscape-ish cells fit a sensor name on one line; squares don't
        const aspect = w / h
        if (aspect < 1 || aspect > 2.2) continue
        const area = w * h
        if (area > bestArea || (area === bestArea && c < bestC)) {
          bestArea = area
          bestC = c
          bestW = w
          bestH = h
        }
      }
      if (!bestArea) {
        // No layout hit the aspect window — fall back to largest square
        const c = Math.max(1, Math.round(Math.sqrt((n * W) / Math.max(1, H))))
        const r = Math.ceil(n / c)
        bestC = c
        bestW = Math.floor((W - Math.max(0, c - 1) * GAP) / c)
        bestH = Math.floor((H - Math.max(0, r - 1) * GAP) / r)
      }
      setGridCols(bestC)
      setCellW(Math.max(28, bestW))
      setCellH(Math.max(24, bestH))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [assets.length])

  React.useEffect(() => {
    let mounted = true

    async function loadOnce() {
      try {
        const list = await fetchSMAssets()
        if (!mounted) return
        setAssets(list)

        // Compute rolling 24h window on each load
        const to = new Date()
        const from = new Date(to.getTime() - 24 * 60 * 60 * 1000)
        const toISO = to.toISOString()
        const fromISO = from.toISOString()

        const ac = new AbortController()
        try {
          const results = await Promise.all(
            list.map(async (a) => {
              const data = await fetchSMSeries(a.id, fromISO, toISO, ac.signal)
              const snap = summarizeLatestSM(data)
              const label = a.name.replace(/_/g, ' ')
              return { id: a.id, name: label, ...snap }
            }),
          )
          if (!mounted) return
          const m = new Map<
            number,
            {
              name: string
              ts?: number
              moisture?: number
              temperature?: number
              salinity?: number
              waterBalance?: number
              oxygen?: number
            }
          >()
          results.forEach((r: any) => m.set(r.id, r))
          setLatest(m)
        } finally {
          ac.abort()
        }
      } catch (e) {
        // silent
      }
    }

    // Initial load
    loadOnce()

    // Auto refresh every 5 minutes
    const interval = setInterval(
      () => {
        loadOnce()
      },
      5 * 60 * 1000,
    )

    return () => {
      mounted = false
      clearInterval(interval)
    }
  }, [])

  const sensors = React.useMemo(() => {
    const arr = Array.from(latest.values())
    arr.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true }),
    )
    return arr
  }, [latest])

  // Header subtitle: how much of the grid is actually reporting this metric,
  // and how recent the newest reading is
  const summary = React.useMemo(() => {
    const reporting = sensors.filter(
      (s) => typeof (s as any)[mode] === 'number',
    ).length
    const newest = sensors.reduce<number | undefined>(
      (acc, s) => (s.ts && (!acc || s.ts > acc) ? s.ts : acc),
      undefined,
    )
    return { total: sensors.length, reporting, updatedAt: formatClock(newest) }
  }, [sensors, mode])

  return (
    <GlassCard
      title={
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}
          >
            <Typography
              noWrap
              sx={{ color: 'white', fontSize: 18, fontWeight: 700 }}
            >
              {MODE_TITLES[mode].title}
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.55)',
                fontSize: 12,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {MODE_TITLES[mode].unit}
            </Typography>
          </Box>
          <Typography
            noWrap
            sx={{
              color: 'rgba(255,255,255,0.62)',
              fontSize: 11,
              lineHeight: 1.3,
              letterSpacing: '0.01em',
            }}
          >
            {`Serapong · ${summary.reporting}/${summary.total} reporting${
              summary.updatedAt ? ` · ${summary.updatedAt}` : ''
            }`}
          </Typography>
        </Box>
      }
      right={
        <ToggleButtonGroup
          color="primary"
          exclusive
          size="small"
          value={mode}
          onChange={handleModeChange}
          sx={{
            '& .MuiToggleButton-root': {
              color: 'rgba(255,255,255,0.75)',
              borderColor: 'rgba(255,255,255,0.25)',
              px: 1.1,
              fontSize: 12,
              lineHeight: 1,
            },
            '& .MuiToggleButton-root.Mui-selected': {
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.14)',
            },
          }}
        >
          {MODE_BUTTONS.map((b) => (
            <ToggleButton key={b.value} value={b.value} title={b.title}>
              {b.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      }
    >
      <Box
        sx={{
          p: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Box
          ref={gridRef}
          sx={{
            display: 'grid',
            flex: 1,
            minHeight: 0,
            width: '100%',
            overflow: 'hidden',
            gridTemplateColumns: `repeat(${gridCols}, ${cellW}px)`,
            gridAutoRows: `${cellH}px`,
            gap: '2px',
            alignContent: 'center',
            justifyContent: 'center',
          }}
        >
          {sensors.map((s, i) => {
            const displayName = cleanSensorName(s.name)
            const value =
              mode === 'moisture'
                ? s.moisture
                : mode === 'waterBalance'
                ? s.waterBalance
                : mode === 'temperature'
                ? s.temperature
                : mode === 'salinity'
                ? s.salinity
                : s.oxygen
            const hasValue = typeof value === 'number'
            const bg =
              mode === 'moisture'
                ? colorForMoisture(s.moisture)
                : mode === 'waterBalance'
                ? colorForWaterBalance(s.waterBalance)
                : mode === 'temperature'
                ? colorForTemp(s.temperature)
                : mode === 'salinity'
                ? colorForSalinity(s.salinity)
                : colorForOxygenPercent(s.oxygen)
            const ink = !hasValue
              ? NO_DATA_INK
              : mode === 'moisture'
              ? inkForMoisture(s.moisture)
              : '#ffffff'
            return (
              <Tooltip
                key={i}
                title={tooltipFor(s)}
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: 16,
                      px: 2,
                      py: 1.25,
                      borderRadius: 1,
                      bgcolor: 'rgba(17,24,39,0.92)',
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
                      whiteSpace: 'pre-line',
                    },
                  },
                  arrow: { sx: { color: 'rgba(17,24,39,0.92)' } },
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 0,
                    backgroundColor: hasValue ? bg : 'transparent',
                    backgroundImage: hasValue ? 'none' : NO_DATA_FILL,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: '2px',
                    px: '4px',
                  }}
                >
                  <Typography
                    sx={{
                      pointerEvents: 'none',
                      color: ink,
                      opacity: hasValue ? 0.85 : 1,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      fontSize: Math.max(8, Math.floor(cellH * 0.16)),
                      lineHeight: 1,
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                    }}
                    title={displayName}
                  >
                    {displayName}
                  </Typography>
                  {hasValue && (
                    <Typography
                      sx={{
                        pointerEvents: 'none',
                        color: ink,
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        fontSize: Math.max(11, Math.floor(cellH * 0.26)),
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                        '& span': { fontSize: '0.7em', opacity: 0.8, ml: '1px' },
                      }}
                    >
                      {formatValue(mode, value as number)}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            )
          })}
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.25,
            px: 1.5,
            py: 1,
            borderTop: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          {LEGENDS[mode].map((item) => (
            <Box
              key={item.label}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '2px',
                  backgroundColor: item.color,
                }}
              />
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.75)',
                  fontSize: 10,
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </GlassCard>
  )
}
